import Vue from 'vue';
import VueRouter from 'vue-router';
import routes from './routes';
import { brandContext } from '../brand/brandContext'; // ⬅ brand helper

Vue.use(VueRouter);

// configure router
const router = new VueRouter({
  routes, // short for routes: routes
  linkActiveClass: 'active',
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) return savedPosition;
    if (to.hash) return { selector: to.hash };
    return { x: 0, y: 0 };
  }
});

const defaultDocumentTitle =
  process.env.VUE_APP_COMPANY_NAME_FRONTEND === 'safedig'
    ? 'Safe Dig Indiana'
    : 'HomeOfTraining';

// Project GLAD has no dashboard — the /dashboard view renders blank for every
// GLAD role. Send any attempt to reach it (direct URL, the "/" redirect, or the
// guest-redirect's { name: 'Dashboard' }) to the pathways screen instead so a
// GLAD user never lands on the empty page.
const isGladBuild = process.env.VUE_APP_COMPANY_NAME_FRONTEND === 'projectglad';
const GLAD_HOME = '/glad/pathways';

router.beforeEach((toRoute, fromRoute, next) => {
  if (isGladBuild && (toRoute.path === '/dashboard' || toRoute.name === 'Dashboard')) {
    return next(GLAD_HOME);
  }
  next();
});

router.beforeEach(async (toRoute, fromRoute, next) => {
  // title
  window.document.title = (toRoute.meta && toRoute.meta.title)
    ? toRoute.meta.title
    : defaultDocumentTitle;

  // brand clearing: ensure HOT theme on default login (skip for Safe Dig - keep its theme)
  if ((toRoute.name === 'Login' || toRoute.path === '/login') && process.env.VUE_APP_COMPANY_NAME_FRONTEND !== 'safedig') {
    brandContext.clear();
  }

  const isAuthenticated = !!localStorage.getItem('hot-token');

  // auth gates
  if (toRoute.meta && toRoute.meta.requiresAuth && !isAuthenticated) {
    // preserve payment-info redirection
    if (toRoute.path === '/payment-information') {
      await Promise.resolve(localStorage.setItem('redirect-path', '/payment-information'));
    }
    return next({ name: 'Login' });
  }

  if (toRoute.meta && toRoute.meta.requiresGuest && isAuthenticated) {
    // if already logged in, go to dashboard
    return next({ name: 'Dashboard' });
  }

  // preserve payment-info redirection even when auth
  if (toRoute.path === '/payment-information') {
    await Promise.resolve(localStorage.setItem('redirect-path', '/payment-information'));
  }

  // capture referral code for marketing
  if (toRoute.query && toRoute.query.ref_code) {
    localStorage.setItem('ref_code', toRoute.query.ref_code);
    const fullPathWithRefCode = `${window.location.origin}/#${toRoute.fullPath}`;
    localStorage.setItem('ref_code_route', fullPathWithRefCode);
  }

  next();
});

import roleAccessMap , { publicRoutes }  from "./../util/roleAccessMap";

router.beforeEach((to, from, next) => {
  console.log(to.path);

  const routeExists = router.getRoutes().some(r => r.path === to.path);
  if (!routeExists) {
    return next();
  }

  // Check if route is public (exact match or dynamic route pattern)
  const isPublicRoute = publicRoutes.includes(to.path) || 
    to.path.startsWith('/hig/') || 
    to.path.startsWith('/company/') ||
    to.path.startsWith('/info/');
  
  const authRequired = !isPublicRoute;
  if (!authRequired) return next();

  const role = localStorage.getItem("hot-user");
  if (!role) {
    return next("/login");
  }
const gladRoles = ['glad_educator', 'glad_trainer', 'glad_rtc_admin', 'glad_ntc_admin'];
  // GLAD routes may declare meta.gladRoles to restrict access to specific
  // roles (e.g. the AI Course Builder is NTC-Admin-only). Enforce that here so
  // an Educator can't reach an admin screen by typing the URL — the backend
  // already 403s those endpoints, this stops the page from rendering at all.
  if (to.meta && Array.isArray(to.meta.gladRoles) && to.meta.gladRoles.length) {
    if (!to.meta.gladRoles.includes(role)) {
      Vue.$toast.error('You do not have access to that page', { timeout: 5000 });
      return next('/dashboard');
    }
    return next();
  }
  if (["super-admin", "sub-admin"].includes(role) || gladRoles.includes(role)) {
    return next();
  }
  // GLAD-features builds (the CCU evaluator tenant) expose the GLAD learner routes
  // to company-admins too. Admin-only GLAD screens are already gated by
  // meta.gladRoles above; the learner routes (pathways/badges/content) carry no
  // gladRoles, so allow them here without per-path allowlisting — the allowlist is
  // exact-match and can't express dynamic paths like /glad/pathways/:id. Reads
  // only: the backend ReadOnlyGuard still 403s any write for evaluator accounts.
  if ((isGladBuild || process.env.VUE_APP_ENABLE_GLAD_FEATURES === 'true') && to.path.startsWith('/glad/')) {
    return next();
  }
  const allowedRoutes = roleAccessMap[role] || [];
  if (!allowedRoutes.includes(to.path)) {
      Vue.$toast.error('Not authorized', {
    timeout: 5000,
  });
  return next("/dashboard");
  }

  // add_employee: employee may only edit their own profile (same id); enforced by redirect
  if (to.path === '/add_employee' && to.query && to.query.id) {
    if (role === 'employee') {
      const currentUserId = localStorage.getItem('hot-user-id');
      if (currentUserId && to.query.id !== currentUserId) {
        Vue.$toast.error('Not authorized', { timeout: 5000 });
        return next('/dashboard');
      }
    }
  }

  return next();
});
export default router;
